import './style.css';
import { SOLAR_MASS } from './engines/constants';
import { orbitalPeriod, orbitalStateAtTime } from './engines/orbital';
import {
  schwarzschildRadius,
  photonSphereRadius,
  iscoRadius,
  timeDilationFactor,
  gravitationalRedshift,
  perihelionPrecessionPerOrbitRad,
  classifySpacetimeRegion,
} from './engines/relativity';

// ============================================================
// 言語切り替え
// ============================================================
const langBtn = document.getElementById('langToggle') as HTMLButtonElement;
let currentLang: 'ja' | 'en' = 'ja';

function syncSliderFill(input: HTMLInputElement) {
  const min = parseFloat(input.min || '0');
  const max = parseFloat(input.max || '100');
  const val = parseFloat(input.value);
  const percent = ((val - min) / (max - min)) * 100;
  input.style.setProperty('--range-progress', `${percent}%`);
}

function applyLang(lang: 'ja' | 'en') {
  currentLang = lang;
  document.documentElement.lang = lang;
  document.querySelectorAll<HTMLElement>('[data-ja]').forEach((el) => {
    el.textContent = lang === 'ja' ? el.getAttribute('data-ja') : el.getAttribute('data-en');
  });
  langBtn.querySelectorAll<HTMLElement>('.opt').forEach((opt) => {
    opt.classList.toggle('on', opt.getAttribute('data-lang-opt') === lang);
  });
  const footerMessageJa = document.getElementById('footerMessageJa');
  const footerMessageEn = document.getElementById('footerMessageEn');
  if (footerMessageJa && footerMessageEn) {
    (footerMessageJa as HTMLElement).style.display = lang === 'ja' ? 'block' : 'none';
    (footerMessageEn as HTMLElement).style.display = lang === 'en' ? 'block' : 'none';
  }
}
langBtn.addEventListener('click', () => applyLang(currentLang === 'ja' ? 'en' : 'ja'));

// ============================================================
// オンボーディング（初回のみ表示、localStorageで記憶）
// ============================================================
const ONBOARDING_KEY = 'cosmos-simulator:onboarding-seen-relativity';
const onboarding = document.getElementById('onboarding') as HTMLElement;
const onboardingDismiss = document.getElementById('onboardingDismiss') as HTMLButtonElement;
const helpBtn = document.getElementById('helpBtn') as HTMLButtonElement;

function showOnboarding() {
  onboarding.classList.remove('hidden');
}
function hideOnboarding() {
  onboarding.classList.add('hidden');
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* localStorageが使えない環境でも動作は継続する */
  }
}
onboardingDismiss.addEventListener('click', hideOnboarding);
helpBtn.addEventListener('click', showOnboarding);

let hasSeenOnboarding = false;
try {
  hasSeenOnboarding = localStorage.getItem(ONBOARDING_KEY) === '1';
} catch {
  hasSeenOnboarding = false;
}
if (hasSeenOnboarding) onboarding.classList.add('hidden');

// ============================================================
// DOM参照
// ============================================================
const massSlider = document.getElementById('massSlider') as HTMLInputElement;
const massVal = document.getElementById('massVal') as HTMLElement;
const aSlider = document.getElementById('aSlider') as HTMLInputElement;
const aVal = document.getElementById('aVal') as HTMLElement;
const eSlider = document.getElementById('eSlider') as HTMLInputElement;
const eVal = document.getElementById('eVal') as HTMLElement;
const warnBox = document.getElementById('warnBox') as HTMLElement;
const canvasWrap = document.getElementById('canvasWrap') as HTMLElement;
const eventHorizon = document.getElementById('eventHorizon') as HTMLElement;
const ringPhoton = document.getElementById('ringPhoton') as HTMLElement;
const ringIsco = document.getElementById('ringIsco') as HTMLElement;
const accretionDisk = document.getElementById('accretionDisk') as HTMLElement;
const testParticle = document.getElementById('testParticle') as HTMLElement;
const trailLayer = document.getElementById('trailLayer') as HTMLElement;
const scaleLabel = document.getElementById('scaleLabel') as HTMLElement;
const precessionInline = document.getElementById('precessionInline') as HTMLElement;
const regionBadge = document.getElementById('regionBadge') as HTMLElement;
const rsVal = document.getElementById('rsVal') as HTMLElement;
const rPhotonVal = document.getElementById('rPhotonVal') as HTMLElement;
const rIscoVal = document.getElementById('rIscoVal') as HTMLElement;
const dilationVal = document.getElementById('dilationVal') as HTMLElement;
const redshiftVal = document.getElementById('redshiftVal') as HTMLElement;
const precessionVal = document.getElementById('precessionVal') as HTMLElement;
const validationLine = document.getElementById('validationLine') as HTMLElement;
const advSwitch = document.getElementById('advSwitch') as HTMLElement;
const formulaBox = document.getElementById('formulaBox') as HTMLElement;

let massSolar = 10;
let aRs = parseFloat(aSlider.value);
let ecc = parseFloat(eSlider.value);
let pxPerRs = 10;

// 質量スライダーは対数スケール（1〜1e7 太陽質量）
function sliderToMass(v: number): number {
  return Math.pow(10, (v / 1000) * 7);
}
function massToSlider(m: number): number {
  return (Math.log10(m) / 7) * 1000;
}

const PRESETS: Record<string, number> = { stellar: 10, sgra: 4.15e6, neutron: 1.4 };
document.querySelectorAll<HTMLButtonElement>('.preset-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const key = btn.getAttribute('data-preset');
    if (!key || !(key in PRESETS)) return;
    massSlider.value = String(massToSlider(PRESETS[key]));
    document.querySelectorAll('.preset-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    onParamsChange();
  });
});

// ============================================================
// トレイル
// ============================================================
const TRAIL_LENGTH = 80;
const trailPool: HTMLElement[] = [];
const trailHistory: { px: number; py: number }[] = [];
for (let i = 0; i < TRAIL_LENGTH; i++) {
  const d = document.createElement('div');
  d.className = 'trail-dot';
  d.style.opacity = '0';
  trailLayer.appendChild(d);
  trailPool.push(d);
}
function updateTrail(px: number, py: number) {
  trailHistory.unshift({ px, py });
  if (trailHistory.length > TRAIL_LENGTH) trailHistory.pop();
  trailHistory.forEach((pos, i) => {
    const dot = trailPool[i];
    if (!dot) return;
    dot.style.left = `calc(50% + ${pos.px}px)`;
    dot.style.top = `calc(50% + ${pos.py}px)`;
    dot.style.opacity = `${(1 - i / TRAIL_LENGTH) * 0.5}`;
  });
}
function clearTrail() {
  trailHistory.length = 0;
  trailPool.forEach((d) => (d.style.opacity = '0'));
}

// ============================================================
// 読み取り値更新
// ============================================================
function formatDistance(m: number): string {
  return m < 1000 ? `${m.toFixed(2)} m` : `${(m / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} km`;
}

function updateReadouts() {
  const MKg = massSolar * SOLAR_MASS;
  const rs = schwarzschildRadius(MKg);
  const rPh = photonSphereRadius(MKg);
  const rIscoM = iscoRadius(MKg);
  const aM = aRs * rs;
  const rPeriapsis = aM * (1 - ecc);

  massVal.textContent = massSolar < 1000 ? massSolar.toFixed(1) : massSolar.toExponential(2);
  aVal.textContent = aRs.toFixed(1);
  eVal.textContent = ecc.toFixed(2);

  rsVal.textContent = formatDistance(rs);
  rPhotonVal.textContent = formatDistance(rPh);
  rIscoVal.textContent = formatDistance(rIscoM);

  const dilation = timeDilationFactor(rPeriapsis, MKg);
  dilationVal.textContent = dilation.toFixed(4);
  const redshift = gravitationalRedshift(rPeriapsis, MKg);
  redshiftVal.textContent = isFinite(redshift) ? redshift.toExponential(3) : '∞';

  const precessionRad = perihelionPrecessionPerOrbitRad(aM, ecc, MKg);
  const precessionDeg = precessionRad * (180 / Math.PI);
  precessionVal.textContent = `${precessionDeg.toFixed(2)}°`;
  precessionInline.textContent = `${precessionDeg.toFixed(1)}°`;

  warnBox.classList.toggle('show', precessionDeg > 45 || aRs < 10);

  const region = classifySpacetimeRegion(rPeriapsis, MKg);
  regionBadge.textContent = region;

  // 水星換算での検証（同じ式を水星のa,eに適用）
  const mercuryA = 5.7909e10;
  const mercuryE = 0.2056;
  const mercuryPeriodYears = 0.2408;
  const mercuryPrecessionRad = perihelionPrecessionPerOrbitRad(mercuryA, mercuryE, SOLAR_MASS);
  const orbitsPerCentury = 100 / mercuryPeriodYears;
  const mercuryArcsecPerCentury = mercuryPrecessionRad * orbitsPerCentury * (180 / Math.PI) * 3600;
  const err = Math.abs((mercuryArcsecPerCentury - 43.0) / 43.0) * 100;
  validationLine.textContent = `43.000″/世紀 (実測) — モデル計算: ${mercuryArcsecPerCentury.toFixed(3)}″/世紀 (誤差${err.toFixed(3)}%)`;
}

function setDiameter(el: HTMLElement, diameterPx: number) {
  el.style.width = `${diameterPx}px`;
  el.style.height = `${diameterPx}px`;
}

function updateScaleAndRings() {
  const MKg = massSolar * SOLAR_MASS;
  const rs = schwarzschildRadius(MKg);
  const apoapsisRs = aRs * (1 + ecc);
  const diskOuterRs = Math.min((iscoRadius(MKg) / rs) * 10, apoapsisRs * 1.3);
  const maxRs = Math.max(apoapsisRs * 1.25, diskOuterRs);

  const rect = canvasWrap.getBoundingClientRect();
  const maxPxRadius = Math.min(rect.width, rect.height) / 2 - 20;
  pxPerRs = maxPxRadius / maxRs;

  scaleLabel.textContent = `1px = ${(1 / pxPerRs).toFixed(3)} r_s`;

  setDiameter(eventHorizon, 2 * pxPerRs * 1.0);
  setDiameter(ringPhoton, 2 * pxPerRs * 1.5);
  setDiameter(ringIsco, 2 * pxPerRs * 3.0);
  setDiameter(accretionDisk, 2 * pxPerRs * diskOuterRs);
}

// ============================================================
// アニメーション（近日点移動を累積して回転させる = rosette軌道）
// ============================================================
let simTimeS = 0;
let lastFrameMs: number | null = null;

function tick(nowMs: number) {
  if (lastFrameMs === null) lastFrameMs = nowMs;
  const dtMs = nowMs - lastFrameMs;
  lastFrameMs = nowMs;

  const MKg = massSolar * SOLAR_MASS;
  const rs = schwarzschildRadius(MKg);
  const aM = aRs * rs;
  const period = orbitalPeriod(aM, MKg);

  // 実時間の経過を「1公転を約3秒で見せる」ペースにマッピング
  const simSecondsPerOrbit = 3.0;
  simTimeS += (dtMs / 1000) * (period / simSecondsPerOrbit);

  const orbitCount = simTimeS / period;
  const precessionPerOrbit = perihelionPrecessionPerOrbitRad(aM, ecc, MKg);
  const accumulatedPeriapsisAngle = orbitCount * precessionPerOrbit;

  const state = orbitalStateAtTime(simTimeS, { semiMajorAxisM: aM, eccentricity: ecc, totalMassKg: MKg });
  const cosW = Math.cos(accumulatedPeriapsisAngle);
  const sinW = Math.sin(accumulatedPeriapsisAngle);
  const xRot = state.x * cosW - state.y * sinW;
  const yRot = state.x * sinW + state.y * cosW;

  const px = (xRot / rs) * pxPerRs;
  const py = (yRot / rs) * pxPerRs;
  testParticle.style.left = `calc(50% + ${px}px)`;
  testParticle.style.top = `calc(50% + ${py}px)`;
  updateTrail(px, py);

  requestAnimationFrame(tick);
}

// ============================================================
// 初期化
// ============================================================
function onParamsChange() {
  massSolar = sliderToMass(parseFloat(massSlider.value));
  aRs = parseFloat(aSlider.value);
  ecc = parseFloat(eSlider.value);
  syncSliderFill(massSlider);
  syncSliderFill(aSlider);
  syncSliderFill(eSlider);
  simTimeS = 0;
  clearTrail();
  updateReadouts();
  updateScaleAndRings();
}

massSlider.addEventListener('input', () => {
  document.querySelectorAll('.preset-btn').forEach((b) => b.classList.remove('active'));
  onParamsChange();
});
aSlider.addEventListener('input', onParamsChange);
eSlider.addEventListener('input', onParamsChange);
window.addEventListener('resize', updateScaleAndRings);

massSlider.value = String(massToSlider(10));
onParamsChange();
requestAnimationFrame(tick);

// ページローダーを隠す
const pageLoader = document.getElementById('pageLoader');
if (pageLoader) {
  requestAnimationFrame(() => {
    pageLoader.classList.add('hide');
    setTimeout(() => pageLoader.remove(), 400);
  });
}

// ============================================================
// フッターメッセージ（クリックで開閉）
// ============================================================
const dedicationFooter = document.getElementById('dedicationFooter');
const footerMessageOverlay = document.getElementById('footerMessageOverlay');
const footerMessageClose = document.getElementById('footerMessageClose');
if (dedicationFooter && footerMessageOverlay && footerMessageClose) {
  dedicationFooter.addEventListener('click', () => {
    footerMessageOverlay.classList.add('show');
  });
  footerMessageClose.addEventListener('click', () => {
    footerMessageOverlay.classList.remove('show');
  });
  footerMessageOverlay.addEventListener('click', (e) => {
    if (e.target === footerMessageOverlay) footerMessageOverlay.classList.remove('show');
  });
}
